import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import constants from 'app/constants'
import fetchJSONData from 'app/util/fetchJSONData'
import Content from 'components/content/Content/Content'
import Layout from 'components/core/Layout/Layout'
import Spinner from 'components/elements/Spinner/Spinner'
import Modal from 'components/interface/Modal/Modal'
import styles from './ModalUtilityTerms.module.scss'

let cache = null

/**
 * The `ModalUtilityTerms` handles the rendering of
 * the sign up modal
 *
 * @returns {React.ReactElement} the element
 */
function ModalUtilityTerms () {
  const [data, setData] = useState(cache)
  const [error, setError] = useState(null)
  const isMounted = useRef(false)
  const bodyRef = useRef()

  const title = useMemo(() => <h1>Utility Terms & Conditions</h1>, [])

  const loadData = useCallback(async () => {
    const { data, success } = await fetchJSONData(
      `${constants.PATH_DATA}/utility_terms.json?ver=${constants.CACHE_BUST_VERSION}`
    )

    if (!isMounted.current) {
      // No longer mounted
      return
    }

    if (!success) {
      setError('There was a problem loading the data. Please try again later.')

      return
    }

    const res = data

    cache = res

    setData(cache)
  }, [])

  const output = useMemo(() => {
    if (!data && !error) {
      return <Spinner />
    }

    if (error) {
      return (
        <div className={styles.error}>
          <p>{error}</p>
        </div>
      )
    }

    return <Content items={data} />
  }, [data, error])

  /**
   * Effect: On mount
   */
  useEffect(() => {
    isMounted.current = true

    if (!cache) {
      loadData()
    }

    return () => {
      isMounted.current = false
    }
  }, [])

  return (
    <Modal.Container size="small">
      <Modal.Header>{title}</Modal.Header>
      <Modal.Body ref={bodyRef} className={styles.content}>
        <Layout.Body justify="center">{output}</Layout.Body>
      </Modal.Body>
      <Modal.Footer />
    </Modal.Container>
  )
}

// Memoize
export default memo(ModalUtilityTerms)
